import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import MobileNav from "@/components/MobileNav";
import {
  ArrowLeft,
  QrCode,
  Share,
  Copy,
  Check,
  Sparkles,
  Palette,
  Image,
  Settings,
  ExternalLink,
  Clock,
  Link as LinkIcon
} from "lucide-react";
import { toast } from "sonner";

const ShareProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { category = "nails" } = useParams();
  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    generateShare();
    fetchProfileData();
  }, [category]);

  const fetchProfileData = async () => {
    try {
      const [prefsRes, boardRes] = await Promise.all([
        axios.get(`${API}/preferences`, { withCredentials: true }),
        axios.get(`${API}/board/current`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setPreferences(prefsRes.data);
      setBoard(boardRes.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const generateShare = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/profiles/${category}/share`,
        { expires_in_hours: 24 },
        { withCredentials: true }
      );
      setShareData(response.data);
    } catch (error) {
      console.error("Error generating share:", error);
      // Fallback - create a mock share link
      setShareData({
        share_id: `${user?.wallet_id || 'PHY-DEMO'}`,
        share_url: `${window.location.origin}/p/${user?.wallet_id || 'demo'}`,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (shareData?.share_url) {
      navigator.clipboard.writeText(shareData.share_url);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareNative = async () => {
    if (navigator.share && shareData?.share_url) {
      try {
        await navigator.share({
          title: "My Phygital Nail Profile",
          text: "Check out my nail preferences!",
          url: shareData.share_url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  const manicurePrefs = preferences?.manicure || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-display text-lg font-semibold">Share Profile</h1>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* QR Code Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-gray-900 to-black p-6 text-white text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5" />
              <span className="font-display font-semibold">My Nail Profile</span>
            </div>
            
            {/* QR Code Display */}
            <div className="bg-white rounded-2xl p-6 mx-auto max-w-xs">
              {loading ? (
                <div className="w-48 h-48 flex items-center justify-center">
                  <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="w-48 h-48 mx-auto flex items-center justify-center bg-gray-100 rounded-xl">
                  {/* Simple QR representation - in production would use actual QR library */}
                  <div className="text-center">
                    <QrCode className="w-32 h-32 text-gray-800 mx-auto" />
                    <p className="text-xs text-gray-600 mt-2 font-mono">{shareData?.share_id}</p>
                  </div>
                </div>
              )}
            </div>
            
            <p className="text-gray-500 text-sm mt-4">
              Show this to your technician
            </p>
          </div>
          
          <CardContent className="p-4 space-y-3">
            {/* Share Link */}
            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-xl">
              <LinkIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700 truncate">
                {shareData?.share_url || "Generating link..."}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={copyLink}
                className="flex-shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="rounded-xl"
                onClick={copyLink}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button 
                className="rounded-xl bg-black hover:bg-gray-800"
                onClick={shareNative}
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            {/* Expiry */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 pt-2">
              <Clock className="w-4 h-4" />
              <span>Link expires in 24 hours</span>
            </div>
          </CardContent>
        </Card>

        {/* Profile Preview */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">What They Will See</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Colors */}
            {board?.selected_colors?.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Favorite Colors
                </p>
                <div className="flex gap-2">
                  {board.selected_colors.slice(0, 5).map((color) => (
                    <div 
                      key={color.color_id}
                      className="w-10 h-10 rounded-lg shadow-sm border border-gray-200"
                      style={{ backgroundColor: color.hex_code }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Designs */}
            {board?.selected_designs?.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                  <Image className="w-4 h-4" /> Saved Designs
                </p>
                <div className="flex gap-2">
                  {board.selected_designs.slice(0, 3).map((design) => (
                    <div 
                      key={design.portfolio_id}
                      className="w-16 h-16 rounded-lg overflow-hidden"
                    >
                      <img 
                        src={design.image_url} 
                        alt={design.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preferences */}
            <div>
              <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" /> Preferences
              </p>
              <div className="flex flex-wrap gap-2">
                {manicurePrefs.shape && manicurePrefs.shape !== "None" && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {manicurePrefs.shape}
                  </span>
                )}
                {manicurePrefs.length && manicurePrefs.length !== "None" && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {manicurePrefs.length}
                  </span>
                )}
                {manicurePrefs.finish && manicurePrefs.finish !== "None" && (
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                    {manicurePrefs.finish}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-100">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">Take it anywhere</h3>
                <p className="text-sm text-purple-700">
                  Your Phygital profile works at any salon. Just show this QR code and your technician can see your preferences instantly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <MobileNav />
    </div>
  );
};

export default ShareProfilePage;
